import { useEscape } from '../lib/useEscape.js';
import * as fmt from '../lib/format.js';
import {
  ENERGY_WINDOW,
  GOAL_LABELS,
  KCAL_PER_KG,
  KCAL_PER_KG_WEEK,
  TREND_WINDOW,
  TREND_WINDOWS,
  goalDefinition,
  type Goal,
} from '../lib/stats.js';

interface Props {
  onClose: () => void;
}

const GOALS: Goal[] = ['loss', 'maintain', 'gain'];

/**
 * Les valeurs affichées sont lues dans `stats.ts` plutôt que recopiées : une
 * page « méthode » qui ment sur le code est pire que pas de page du tout.
 */
const RULES: Array<{ title: string; body: string }> = [
  {
    title: 'Rien n’est interpolé',
    body: 'Un jour sans mesure reste un trou. La courbe se coupe, les moyennes comptent les jours connus, et la tendance élargit sa marge au lieu d’inventer une valeur.',
  },
  {
    title: 'Poids lissé — moyenne 7 jours',
    body: 'Moyenne des sept derniers jours, exigée à quatre jours renseignés minimum. Elle est calculée en arrière, donc en retard d’environ trois jours sur le signal réel : c’est le prix d’une valeur disponible aujourd’hui plutôt que dans trois jours.',
  },
  {
    title: `Tendance — régression sur ${TREND_WINDOWS.join(', ')} jours`,
    body: `Droite des moindres carrés sur les pesées de la fenêtre (${TREND_WINDOW} jours par défaut), pente ramenée à la semaine. Un jour manquant n’est pas rejeté : il réduit l’étalement des abscisses, donc la précision, ce que la marge affiche.`,
  },
  {
    title: 'Marge — intervalle de confiance à 95 %',
    body: 'L’écart-type résiduel est plancheré à 0,4 % du poids corporel, la variabilité d’une pesée à l’autre due à l’eau, au glycogène et au transit. Sans ce plancher, quelques pesées fortuitement alignées annonceraient une certitude que la physiologie n’autorise pas. Sur cinq jours la marge dépasserait ±2 kg/semaine — c’est pourquoi la fenêtre la plus courte proposée ici est de quatorze jours.',
  },
  {
    title: 'Résidus corrélés — correction de l’autocorrélation',
    body: 'Une rétention d’eau dure plusieurs jours : deux écarts consécutifs à la droite de tendance se ressemblent, et les moindres carrés ordinaires, qui supposent l’inverse, annoncent une précision qu’ils n’ont pas. Ephemeris estime la corrélation ρ entre résidus de jours consécutifs, corrigée de son biais de petit échantillon, et en déduit un effectif indépendant équivalent n(1−ρ)/(1+ρ). La marge est élargie de √((1+ρ)/(1−ρ)) et les degrés de liberté réduits d’autant. Avec ρ = 0,4, vingt-huit pesées n’en valent que douze.',
  },
  {
    title: 'Séance de renforcement',
    body: "Une séance retient de l’eau : le glycogène resynthétisé en fixe 3 à 4 grammes par gramme, et l’œdème inflammatoire culmine entre 24 et 72 heures. C’est le principal perturbateur structuré de la balance, et il est modélisé comme tel. La case cochée sur une journée pèse sur les trois pesées qui suivent, selon des poids fixés d’avance par la physiologie plutôt qu’ajustés : le noyau couvre ainsi les 72 heures en ne coûtant qu’un seul degré de liberté. Seule son amplitude est estimée, et seulement lorsque la fenêtre contient assez de jours avec et sans séance. Retirer cette composante des résidus resserre l’intervalle de la tendance — c’est le vrai bénéfice, au-delà d’expliquer une journée. Les journées antérieures à l’apparition de la case ne sont jamais utilisées : y lire « pas de séance » là où l’on ne sait rien écraserait l’amplitude vers zéro.",
  },
  {
    title: 'Combien de jours avant de trancher',
    body: "Quand l’intervalle est trop large, la carte annonce le nombre de jours de pesée quotidienne qui manquent encore. Ce n’est pas une estimation au doigt mouillé : on cherche la première fenêtre, et la première date, où la demi-marge passerait sous la demi-largeur de la bande, en supposant le bruit et l’autocorrélation inchangés. Deux leviers y concourent — boucher les trous de la fenêtre courante, et atteindre une fenêtre plus longue — et le calcul les mélange comme la réalité le fait.",
  },
  {
    title: 'Verdict — trois états',
    body: 'Dans la cible, hors cible, ou « trop incertain pour trancher » lorsque l’intervalle de confiance couvre toute la bande de tolérance et en déborde des deux côtés. Colorer en vert ou en rouge une pente que les données ne soutiennent pas serait une information fausse présentée comme sûre.',
  },
  {
    title: `Maintien — bilan énergétique sur ${ENERGY_WINDOW} jours`,
    body: `Apport moyen déclaré, corrigé de la variation des réserves : dépense ≈ apport moyen − pente en kg/jour × ${KCAL_PER_KG.toLocaleString('fr-FR')}. Méthode dite « intake-balance ». Son intérêt tient à ce qu’elle absorbe le biais de déclaration : une sous-estimation des portions produit un maintien sous-estimé d’autant, et l’ajustement conseillé reste juste dans l’unité de comptage de l’utilisateur. Il faut ${Math.ceil(ENERGY_WINDOW / 2)} jours d’apports renseignés pour qu’elle s’active.`,
  },
  {
    title: 'Maintien — modèle de repli',
    body: 'Tant que les apports manquent, la dépense est prédite par Mifflin-St Jeor — 10 × poids + 6,25 × taille − 5 × âge, plus une constante liée au sexe — multipliée par 1,27 pour la thermogenèse alimentaire et l’activité incompressible, à quoi s’ajoute le coût net de la marche : 0,0005 kcal par kilo et par mètre, la distance étant déduite des pas et d’une longueur de foulée de 0,415 × taille. Sans taille ni âge au profil, ce modèle retombe sur un forfait de 21 kcal/kg, d’une précision médiocre.',
  },
  {
    title: 'Équivalence calorique',
    body: `1 kg de masse corporelle ≈ ${KCAL_PER_KG.toLocaleString('fr-FR')} kcal, soit ${KCAL_PER_KG_WEEK.toLocaleString('fr-FR')} kcal/jour pour 1 kg/semaine. Elle sert à lire une pente déjà observée, jamais à projeter : la dépense baisse à mesure que le poids baisse, si bien que la règle surestimerait une perte future d’un facteur proche de deux.`,
  },
  {
    title: 'Bilan par semaine',
    body: 'Le Δ compare deux moyennes sur sept jours, jamais deux pesées. D’abord parce qu’une différence de moyennes divise le bruit par environ √7. Ensuite parce que le poids suit un rythme hebdomadaire — haut le dimanche et le lundi, bas le vendredi — et qu’un bloc de sept jours contient chaque jour de la semaine une fois, ce qui annule ce rythme.',
  },
];

const SOURCES: Array<{ label: string; note: string; url: string }> = [
  {
    label: 'Hall et al., The Lancet, 2011',
    note: 'Dynamique du bilan énergétique ; pourquoi la règle des 7700 kcal ne vaut pas pour projeter.',
    url: 'https://pubmed.ncbi.nlm.nih.gov/21872751/',
  },
  {
    label: 'Thomas et al., Int J Obesity, 2013',
    note: 'Quantification de l’erreur de la règle « 3500 kcal par livre ».',
    url: 'https://www.nature.com/articles/ijo2013112',
  },
  {
    label: 'Sanghvi, Redman, Martin, Ravussin & Hall, AJCN, 2015',
    note: 'Validation de la méthode intake-balance contre l’eau doublement marquée.',
    url: 'https://pubmed.ncbi.nlm.nih.gov/26040640/',
  },
  {
    label: 'Lichtman et al., NEJM, 1992',
    note: 'Sous-déclaration des apports, de l’ordre de 47 % dans les cas extrêmes.',
    url: 'https://www.nejm.org/doi/full/10.1056/NEJM199212313272701',
  },
  {
    label: 'Orsama et al., Obesity Facts, 2014',
    note: 'Rythme hebdomadaire du poids : haut le dimanche-lundi, bas le vendredi.',
    url: 'https://karger.com/ofa/article/7/1/36/240088/Weight-Rhythms-Weight-Increases-during-Weekends',
  },
  {
    label: 'Variabilité euvolémique du poids, Renal Failure, 2023',
    note: 'Écart-type d’environ 0,5 % du poids d’un jour à l’autre, sur 9521 jours standardisés.',
    url: 'https://pubmed.ncbi.nlm.nih.gov/37955103/',
  },
  {
    label: 'Garthe et al., IJSNEM, 2011',
    note: '0,7 %/semaine préserve la masse maigre, 1,4 %/semaine non : d’où des bandes en pourcentage du poids.',
    url: 'https://pubmed.ncbi.nlm.nih.gov/21558571/',
  },
  {
    label: 'Helms et al., recommandations pour athlètes naturels',
    note: 'Bande de prise de poids, 0,25 à 0,5 %/semaine.',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC7052702/',
  },
  {
    label: 'Frankenfield et al., J Am Diet Assoc, 2005',
    note: 'Mifflin-St Jeor, l’équation prédictive la plus exacte du métabolisme de repos chez l’adulte.',
    url: 'https://pubmed.ncbi.nlm.nih.gov/15883556/',
  },
  {
    label: 'Santer et al., JGR, 2000',
    note: 'Effectif indépendant équivalent pour une tendance à résidus autocorrélés.',
    url: 'https://agupubs.onlinelibrary.wiley.com/doi/10.1029/1999JD901105',
  },
  {
    label: 'Olsson & Saltin, Acta Physiol Scand, 1970',
    note: 'Le glycogène fixe 3 à 4 g d’eau par gramme : d’où le poids retenu après une séance.',
    url: 'https://onlinelibrary.wiley.com/doi/abs/10.1111/j.1748-1716.1970.tb04764.x',
  },
  {
    label: 'Glycogène et statut hydrique, Nutrients, 2023',
    note: 'Revue des mesures du glycogène musculaire et de l’eau qui l’accompagne.',
    url: 'https://www.mdpi.com/2072-6643/15/1/155',
  },
  {
    label: 'Pontzer et al., Current Biology, 2016',
    note: 'Dépense totale contrainte : l’activité ne s’ajoute pas linéairement à la dépense.',
    url: 'https://pubmed.ncbi.nlm.nih.gov/26832439/',
  },
];

export function MethodModal({ onClose }: Props) {
  useEscape(onClose);

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-label="Méthode de calcul"
      onClick={onClose}
    >
      <div className="modal__box" onClick={(event) => event.stopPropagation()}>
        <header className="modal__head">
          <div>
            <div className="eyebrow">Méthode</div>
            <h2 className="modal__title">Règles de calcul et sources</h2>
          </div>
          <button type="button" className="btn--icon" onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </header>

        <p className="modal__lead">
          Tout ce qui est affiché dans Ephemeris sort des trois chiffres saisis et des règles
          ci-dessous. Elles sont discutables — c’est pour cela qu’elles sont écrites.
        </p>

        <div className="modal__rows">
          {RULES.map((rule) => (
            <div key={rule.title}>
              <h3>{rule.title}</h3>
              <p>{rule.body}</p>
            </div>
          ))}

          <div>
            <h3>Bandes d’objectif, en pourcentage du poids</h3>
            <table className="method__table">
              <thead>
                <tr>
                  <th>Objectif</th>
                  <th>Cible</th>
                  <th>Bande tolérée</th>
                </tr>
              </thead>
              <tbody>
                {GOALS.map((goal) => {
                  const definition = goalDefinition(goal);
                  return (
                    <tr key={goal}>
                      <td>{GOAL_LABELS[goal]}</td>
                      <td>{fmt.signedPercent(definition.target)}/sem</td>
                      <td>
                        {fmt.signedPercent(definition.low)} à {fmt.signedPercent(definition.high)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p>
              Exprimées relativement au poids et non en kilos : −0,5 kg/semaine vaut 0,9 %/semaine à
              55 kg et 0,4 %/semaine à 120 kg, deux régimes sans rapport.
            </p>
          </div>

          <div>
            <h3>Sources</h3>
            <ul className="method__sources">
              {SOURCES.map((source) => (
                <li key={source.url}>
                  <a href={source.url} target="_blank" rel="noreferrer noopener">
                    {source.label}
                  </a>
                  <span>{source.note}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3>Ce qu’Ephemeris ne fait pas</h3>
            <p>
              Il ne projette aucune date d’objectif, ne prescrit aucun régime et ne prétend pas
              mesurer la composition corporelle : trois chiffres quotidiens ne le permettent pas.
              Les estimations de dépense, en particulier, décrivent une moyenne sur plusieurs
              semaines et ne disent rien d’une journée isolée.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
